import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpRight, QrCode, ShieldCheck, Smartphone, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { PcUser, queryLoginQr, requestLoginQr, storePcLogin } from '../lib/pcApi';

interface LoginModalProps {
  notice?: string;
  onClose: () => void;
  onLoggedIn: (user: PcUser) => void;
}

const POLL_INTERVAL_MS = 10_000;

export default function LoginModal({ onClose, onLoggedIn, notice }: LoginModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [qrCode, setQrCode] = useState('');
  const [qrState, setQrState] = useState<'loading' | 'waiting' | 'expired' | 'error'>('loading');
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    const controller = new AbortController();
    let timer: number | undefined;
    const schedule = (task: () => void, delay = POLL_INTERVAL_MS) => {
      if (!controller.signal.aborted) timer = window.setTimeout(task, delay);
    };

    const poll = async (code: string) => {
      if (controller.signal.aborted) return;
      const startedAt = Date.now();
      const scheduleNextPoll = () => schedule(
        () => { void poll(code); },
        Math.max(0, POLL_INTERVAL_MS - (Date.now() - startedAt)),
      );
      try {
        const result = await queryLoginQr(code, controller.signal);
        if (controller.signal.aborted) return;
        if (result.qrStatus === 3) {
          try {
            if (!result.loginInfo) throw new Error('QRコードの読み取りに成功しましたが、ログイン情報を取得できませんでした。');
            onLoggedIn(storePcLogin(result.loginInfo));
          } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'ログイン情報が無効です。もう一度QRコードを取得してください。');
            setQrState('error');
            schedule(() => { void start(true); });
          }
          return;
        }
        if (result.qrStatus === 2) {
          setQrCode('');
          setQrState('expired');
          schedule(() => { void start(true); });
          return;
        }
        setError('');
        setQrState('waiting');
        scheduleNextPoll();
      } catch (reason) {
        if (controller.signal.aborted) return;
        setError(reason instanceof Error ? reason.message : 'QRコードの状態確認に失敗しました。');
        setQrState('error');
        scheduleNextPoll();
      }
    };

    const start = async (quiet = false) => {
      const startedAt = Date.now();
      if (!quiet) {
        setQrState('loading');
        setQrCode('');
        setError('');
      }
      try {
        const code = await requestLoginQr(controller.signal);
        if (controller.signal.aborted) return;
        setQrCode(code);
        setError('');
        setQrState('waiting');
        schedule(() => { void poll(code); });
      } catch (reason) {
        if (controller.signal.aborted) return;
        setError(reason instanceof Error ? reason.message : 'QRコードの取得に失敗しました。時間をおいて再度お試しください。');
        setQrState('error');
        schedule(
          () => { void start(true); },
          Math.max(0, POLL_INTERVAL_MS - (Date.now() - startedAt)),
        );
      }
    };
    void start();
    return () => {
      controller.abort();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [attempt]);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-modal-title"
        className="relative grid max-h-[calc(100dvh-32px)] w-full max-w-[740px] overflow-y-auto [scrollbar-gutter:stable] rounded-[28px] bg-[#F5F8F7] text-[#172421] shadow-[0_28px_90px_rgba(0,0,0,0.55)] md:grid-cols-[1.1fr_0.9fr]"
      >
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          aria-label="ログインウィンドウを閉じる"
          className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/5 text-[#26322F] transition hover:bg-black/10"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="min-w-0 px-6 pb-7 pt-8 sm:px-9 sm:pb-9 sm:pt-10">
          <div className="mb-6 flex items-center gap-2.5">
            <img src="/logo.webp" alt="" className="h-9 w-9 rounded-xl object-cover" />
            <span className="text-lg font-black tracking-tight">Koemori</span>
          </div>
          <h2 id="login-modal-title" className="text-2xl font-black tracking-tight">アプリでログイン</h2>
          {notice && <p role="alert" className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900">{notice}</p>}
          <p className="mt-2 max-w-sm text-sm leading-6 text-[#62716C]">
            KoemoriアプリでQRコードをスキャンし、アプリ内でログインを承認してください。
          </p>

          <div className="mt-6 flex h-[392px] min-w-0 flex-col items-center rounded-2xl border border-[#DAE6E2] bg-white p-5 text-center shadow-sm">
            <div aria-busy={qrState === 'loading'} className="relative flex h-44 w-44 shrink-0 items-center justify-center rounded-xl border border-[#E7EEEB] bg-[linear-gradient(90deg,#F0F4F2_1px,transparent_1px),linear-gradient(#F0F4F2_1px,transparent_1px)] bg-[size:16px_16px]">
              {qrCode ? <QRCodeSVG value={qrCode} size={152} marginSize={2} className="rounded bg-white" /> : <QrCode className="h-28 w-28 text-[#19362F]/35" strokeWidth={1.5} aria-hidden="true" />}
            </div>
            <div className="mt-4 h-24 w-full min-w-0">
              <p className="text-sm font-bold">{qrState === 'loading' ? 'QRコードを取得中…' : qrState === 'expired' ? 'QRコードの有効期限が切れました' : qrState === 'error' ? 'QRコードログインは現在ご利用いただけません' : 'アプリでQRコードを読み取って承認してください'}</p>
              <p role={error ? 'alert' : undefined} title={error || undefined} className="mt-2 h-10 overflow-hidden break-words text-xs leading-5 text-red-600">{error || '\u00a0'}</p>
              <p className={`text-xs text-[#687872] ${qrState === 'expired' || qrState === 'error' ? '' : 'invisible'}`}>10秒ごとに自動で再試行されます。手動で更新することも可能です。</p>
            </div>
            <button type="button" onClick={() => setAttempt((value) => value + 1)} className="mt-auto rounded-full bg-[#0F2923] px-4 py-2 text-xs font-bold text-white">QRコードを再読み込み</button>
          </div>

          <p className="mt-5 text-xs leading-5 text-[#687872]">
            ログインするとアーティスト申請が可能です。認証バンドは楽曲をアップロードできます。
          </p>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold text-[#126D60]">
            <a href="https://apps.apple.com/jp/app/koemori/id6801087619" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline">App Store <ArrowUpRight className="h-3 w-3" /></a>
            <a href="https://play.google.com/store/apps/details?id=com.meetune.koemori" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline">Google Play <ArrowUpRight className="h-3 w-3" /></a>
          </div>

        </div>

        <div className="relative hidden min-h-full min-w-0 flex-col overflow-hidden bg-gradient-to-br from-[#C4F6EC] via-[#9EE5EF] to-[#80ABEE] p-8 md:flex">
          <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-white/25 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-[#35D6D2]/20 blur-3xl" />
          <div className="relative">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/55 text-[#146B5D]"><Smartphone className="h-5 w-5" /></div>
            <p className="mt-4 text-sm font-black tracking-[0.14em] text-[#174D47]">アプリでQRコードをスキャン</p>
            <p className="mt-2 text-sm leading-6 text-[#315F62]">アプリでQRコードをスキャンして承認すると、PC側にアイコンとニックネームが表示されます。</p>
          </div>
          <div className="relative flex min-h-[390px] flex-1 items-center justify-center py-8" aria-hidden="true">
            <div className="pointer-events-none absolute h-[268px] w-[268px] rounded-full border border-white/35" />
            <div className="pointer-events-none absolute h-[340px] w-[340px] rounded-full border border-white/20" />
            <div className="relative w-[216px] rounded-[38px] border-[6px] border-[#294550] bg-[#081616] p-[6px] shadow-[0_26px_55px_rgba(23,70,85,0.35),inset_0_1px_1px_rgba(255,255,255,0.25)]">
              <div className="overflow-hidden rounded-[27px] bg-gradient-to-b from-[#1B3330] to-[#0C1C1B] px-4 pb-5 pt-3 text-white">
                <div className="flex items-center justify-between text-[9px] font-semibold text-white/70">
                  <span>9:41</span>
                  <span className="flex items-center gap-1">
                    <span className="h-1.5 w-1 rounded-full bg-white/70" />
                    <span className="h-1.5 w-1.5 rounded-full bg-white/70" />
                    <span className="h-1.5 w-2.5 rounded-[3px] border border-white/70" />
                  </span>
                </div>
                <div className="mt-5 flex items-center gap-2">
                  <img src="/logo.webp" alt="" className="h-7 w-7 rounded-lg object-cover" />
                  <span className="text-[13px] font-black tracking-tight">Koemori</span>
                </div>
                <div className="mt-5 text-center">
                  <p className="text-[9px] font-bold tracking-[0.16em] text-[#7BEFDE]">SAFE SIGN IN</p>
                  <p className="mt-1 text-sm font-black">QRコードを読み取って、かんたんログイン</p>
                </div>
                <div className="mt-4 flex h-[132px] flex-col items-center justify-center rounded-2xl border border-[#56E8D7]/45 bg-[#1B514A]/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]">
                  <div className="flex h-[76px] w-[76px] items-center justify-center rounded-xl bg-[#D9FFF8]/10">
                    <QrCode className="h-12 w-12 text-[#00F5D4]" strokeWidth={1.9} />
                  </div>
                  <span className="mt-2 text-[10px] font-bold text-white/90">QRコードをスキャン</span>
                </div>
                <div className="mt-4 flex items-center gap-2 rounded-xl bg-white/[0.06] px-2.5 py-2 text-[9px] font-medium text-white/75">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-[#69EAD9]" />
                  <span>アプリ内で承認して、安全にログイン</span>
                </div>
              </div>
              <div className="mx-auto mt-2 h-1 w-12 rounded-full bg-white/30" />
            </div>
          </div>
        </div>
      </section>
    </div>,
    document.body
  );
}

